// src/pages/AdminUsersPage.js
import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";

const EMPTY_FORM = { username: "", full_name: "", email: "", password: "", confirm_password: "", roles: [] };

export default function AdminUsersPage() {
  const { user: currentUser } = useContext(AuthContext);

  const [users, setUsers]         = useState([]);
  const [allRoles, setAllRoles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [actionError, setActionError] = useState("");

  const [modal, setModal]         = useState(null); // "add" | "edit" | "password"
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [toggling, setToggling]   = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/roles"),
      ]);
      setUsers(usersRes.data.items);
      setAllRoles(rolesRes.data.items);
    } catch {
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openModal(type, user = null) {
    setActionError("");
    setEditingUser(user);
    setForm(
      type === "edit"
        ? { username: user.username ?? "", full_name: user.full_name, email: user.email ?? "", password: "", confirm_password: "", roles: user.roles ?? [] }
        : EMPTY_FORM
    );
    setModal(type);
  }

  function toggleRole(name) {
    setForm((p) => ({
      ...p,
      roles: p.roles.includes(name) ? p.roles.filter((r) => r !== name) : [...p.roles, name],
    }));
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.full_name.trim() || !form.password) {
      setActionError("Username, full name, and password are required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/users", {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        roles: form.roles,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.full_name.trim()) {
      setActionError("Username and full name are required.");
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        roles: form.roles,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update user.");
    } finally {
      setSaving(false);
    }
  }

  async function handlePassword(e) {
    e.preventDefault();
    // Resets always go to the fixed temporary password; the admin never types
    // one. The backend ignores any body and applies the temp password itself.
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/users/${editingUser.id}/password`);
      closeModal();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to reset password.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(user) {
    setToggling(user.id);
    try {
      await api.put(`/admin/users/${user.id}`, { is_active: !user.is_active });
      await load();
    } catch {
      setError("Failed to update user status.");
    } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-people-fill me-2" />
          Users
        </h3>
        <Button size="sm" className="ms-auto" onClick={() => openModal("add")}>
          <i className="bi bi-plus-lg me-1" />Add User
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Username</th>
            <th>Full Name</th>
            <th>Email</th>
            <th>Roles</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isSelf = u.id === currentUser?.id;
            const isBusy = toggling === u.id;
            return (
              <tr key={u.id} className={!u.is_active ? "table-secondary" : ""}>
                <td className="align-middle fw-semibold">{u.username}</td>
                <td className="align-middle">{u.full_name}</td>
                <td className="align-middle text-muted">{u.email}</td>
                <td className="align-middle">
                  <div className="d-flex gap-1 flex-wrap">
                    {u.roles.length === 0
                      ? <span className="text-muted fst-italic" style={{ fontSize: "0.85rem" }}>None</span>
                      : u.roles.map((r) => (
                          <Badge key={r} bg="secondary" style={{ fontSize: "0.7rem" }}>
                            {r}
                          </Badge>
                        ))}
                  </div>
                </td>
                <td className="align-middle">
                  <div className="d-flex gap-1 flex-wrap">
                    {u.is_active
                      ? <Badge bg="success">Active</Badge>
                      : <Badge bg="secondary">Disabled</Badge>}
                    {u.must_change_password && (
                      <Badge bg="warning" text="dark">Pwd Reset</Badge>
                    )}
                  </div>
                </td>
                <td className="align-middle">
                  <div className="d-flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline-primary" onClick={() => openModal("edit", u)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => openModal("password", u)}>
                      Reset Password
                    </Button>
                    {!isSelf && (
                      <Button
                        size="sm"
                        variant={u.is_active ? "outline-danger" : "outline-success"}
                        disabled={isBusy}
                        onClick={() => handleToggleActive(u)}
                      >
                        {isBusy
                          ? <Spinner animation="border" size="sm" />
                          : u.is_active ? "Disable" : "Enable"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Add User Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Username <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.username} onChange={field("username")} placeholder="jsmith" autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.full_name} onChange={field("full_name")} placeholder="Jane Smith" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={field("email")} placeholder="jane@example.com" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password <span className="text-danger">*</span></Form.Label>
              <Form.Control type="password" value={form.password} onChange={field("password")} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Roles</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {allRoles.map((r) => (
                  <Form.Check
                    key={r.name}
                    type="checkbox"
                    id={`add-role-${r.name}`}
                    label={r.name}
                    checked={form.roles.includes(r.name)}
                    onChange={() => toggleRole(r.name)}
                  />
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Create User"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Username <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.username} onChange={field("username")} autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.full_name} onChange={field("full_name")} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={field("email")} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Roles</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {allRoles.map((r) => (
                  <Form.Check
                    key={r.name}
                    type="checkbox"
                    id={`edit-role-${r.name}`}
                    label={r.name}
                    checked={form.roles.includes(r.name)}
                    onChange={() => toggleRole(r.name)}
                  />
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={modal === "password"} onHide={closeModal} centered>
        <Form onSubmit={handlePassword}>
          <Modal.Header closeButton>
            <Modal.Title>Reset Password — {editingUser?.full_name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <p className="mb-2">
              This resets <strong>{editingUser?.full_name}</strong>’s password to the
              temporary password{" "}
              <code>hhh2026</code>.
            </p>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              They’ll be signed out of any active sessions and prompted to set a new
              password the next time they log in.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="warning" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Reset to hhh2026"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
